import React from "react";
import { Route, Redirect, RouteProps } from "react-router-dom";
import * as FeatherIcon from "react-feather";

import { isUserAuthenticated } from "../helpers/authUtils";
import { startsWith } from "lodash";
import UserInfo from "../pages/auth/UserInfo";
import { QuestionsType } from "../constants/bilet-constant";

// auth
const Login = React.lazy(() => import("../pages/auth/Login"));
const Logout = React.lazy(() => import("../pages/auth/Logout"));
const LogoutAndRegister = React.lazy(
  () => import("../pages/auth/LogoutAndRegister")
);
const Register = React.lazy(() => import("../pages/auth/Register"));
const ForgetPassword = React.lazy(() => import("../pages/auth/ForgetPassword"));
const ConfirmPassword = React.lazy(
  () => import("../pages/auth/ConfirmPassword")
);
// notFound
const NotFound = React.lazy(() => import("../pages/dashboard/NotFound"));

// Orders
const OrdersList = React.lazy(() => import("../pages/orders/OrdersList"));
const OrdersAdd = React.lazy(() => import("../pages/orders/OrdersAdd"));
// Student
const StudentList = React.lazy(
  () => import("../pages/bilet/student/StudentList")
);
const StudentDetail = React.lazy(
  () => import("../pages/bilet/student/StudentDetail")
);

// Student
const TeacherList = React.lazy(
  () => import("../pages/bilet/student/StudentList")
);
const TeacherDetail = React.lazy(
  () => import("../pages/bilet/student/StudentDetail")
);

// book
const BookList = React.lazy(() => import("../pages/bilet/book/BookList"));
const BookAdd = React.lazy(() => import("../pages/bilet/book/BookAdd"));

// Packages
const PackagesList = React.lazy(
  () => import("../pages/bilet/packages/PackagesList")
);
const PackagesAdd = React.lazy(
  () => import("../pages/bilet/packages/PackagesAdd")
);

// Diary
const DiaryList = React.lazy(() => import("../pages/bilet/diary/DiaryList"));

// TopicCategory
const TopicCategoryList = React.lazy(
  () => import("../pages/bilet/topic_category/TopicCategoryList")
);

// PairOfWord
const PairOfWordList = React.lazy(
  () => import("../pages/bilet/pair_of_word/PairOfWordList")
);
const PairOfWordAdd = React.lazy(
  () => import("../pages/bilet/pair_of_word/PairOfWordAdd")
);

// Sentence
const SentenceList = React.lazy(
  () => import("../pages/bilet/sentence/SentenceList")
);
const SentenceAdd = React.lazy(
  () => import("../pages/bilet/sentence/SentenceAdd")
);

// Quiz
const QuizList = React.lazy(() => import("../pages/bilet/quiz/QuizList"));

// Question
const QuestionList = React.lazy(
  () => import("../pages/bilet/question/QuestionList")
);
const QuestionAdd = React.lazy(
  () => import("../pages/bilet/question/QuestionAdd")
);
const ChooseAdd = React.lazy(
  () => import("../pages/bilet/question/components/ChooseAdd")
);
const DragDropAdd = React.lazy(
  () => import("../pages/bilet/question/components/DragDropAdd")
);
const ReadingAdd = React.lazy(
  () => import("../pages/bilet/question/components/ReadingAdd")
);
const FillingAdd = React.lazy(
  () => import("../pages/bilet/question/components/FillingAdd")
);
const ArrangeAdd = React.lazy(
  () => import("../pages/bilet/question/components/ArrangeAdd")
);

const ChooseAddSelect = React.lazy(
  () => import("../pages/bilet/question/components_select/ChooseAddSelect")
);

// PairOfWordRequest
const PairOfWordRequestList = React.lazy(
  () => import("../pages/bilet/pair-of-word-request/PairOfWordRequestList")
);

// Users
const UsersList = React.lazy(() => import("../pages/user/UsersList"));
const UsersAdd = React.lazy(() => import("../pages/user/UsersAdd"));
const UsersDetail = React.lazy(() => import("../pages/user/UsersDetail"));

// Roles
const RolesList = React.lazy(() => import("../pages/roles/RolesList"));
const RoleAdd = React.lazy(() => import("../pages/roles/RoleAdd"));

// Quote
const QuoteList = React.lazy(
  () => import("../pages/administration/quote/QuoteList")
);
const QuoteAdd = React.lazy(
  () => import("../pages/administration/quote/QuoteAdd")
);

// SaleGroup
const SaleGroupList = React.lazy(
  () => import("../pages/sale_group/SaleGroupList")
);
const SaleGroupAdd = React.lazy(
  () => import("../pages/sale_group/SaleGroupAdd")
);
const SaleGroupDetail = React.lazy(
  () => import("../pages/sale_group/SaleGroupDetail")
);
const SaleGroupMember = React.lazy(
  () => import("../pages/sale_group/SaleGroupMember")
);

// Commission
const CommissionList = React.lazy(
  () => import("../pages/commission/CommissionList")
);
const CommissionDetail = React.lazy(
  () => import("../pages/commission/CommissionDetail")
);

// Report
const ReportRevenues = React.lazy(
  () => import("../pages/report/ReportRevenues")
);
const ReportOrders = React.lazy(() => import("../pages/report/ReportOrders"));
const ReportCommission = React.lazy(
  () => import("../pages/report/ReportCommission")
);

// App Configs
const AppConfigs = React.lazy(
  () => import("../pages/administration/app_configs/AppConfigs")
);
// FAQ
const FAQList = React.lazy(() => import("../pages/administration/faq/FAQList"));
const FAQAdd = React.lazy(() => import("../pages/administration/faq/FAQAdd"));

// homepage
const HomePage = React.lazy(() => import("../pages/homepage/HomePage"));

// Website
const CategoryList = React.lazy(
  () => import("../pages/website/news/category/CategoryList")
);
const CategoryAdd = React.lazy(
  () => import("../pages/website/news/category/CategoryAdd")
);

// Website POSTs
const PostList = React.lazy(
  () => import("../pages/website/news/post/PostList")
);
const PostAdd = React.lazy(() => import("../pages/website/news/post/PostAdd"));

// Website Menu
const MenuShow = React.lazy(
  () => import("../pages/website/news/menu/MenuShow")
);

interface PrivateRouteProps extends RouteProps {
  component: any;
  roles: any;
  user: any;
  access_token: string;
  path: string;
  dataFromRouter?: any;
}

const PrivateRoute = (props: PrivateRouteProps) => {
  const {
    component: Component,
    roles,
    user,
    access_token,
    path,
    dataFromRouter,
    ...rest
  } = props;
  return (
    <Route
      {...rest}
      render={(props) => {
        if (startsWith(path, "/iframes")) {
          return <Component {...props} dataFromRouter={dataFromRouter} />;
        }
        if (!isUserAuthenticated() || !access_token) {
          // not logged in so redirect to login page with the return url
          return (
            <Redirect
              to={{
                pathname: "/account/login",
                state: { from: props.location },
              }}
            />
          );
        }
        return <Component {...props} dataFromRouter={dataFromRouter} />;
        // if (checkRole(user?.roles, roles)) {
        //   return <Component {...props} dataFromRouter={dataFromRouter} />;
        // } else {
        //   return <Redirect to={{ pathname: "/account" }} />;
        // }
        // const role = user?.roles?user?.roles[0]:'anonymous'
        // // check if route is restricted by role
        // // console.log('PrivateRoute',roles,role,user,access_token)
        // if (roles && roles.indexOf(role) === -1) {
        //     // role not authorised so redirect to home page
        //     return <Redirect to={{ pathname: '/account' }} />;
        // }

        // // authorised so return component
        // return <Component {...props} />;
      }}
    />
  );
};

// root routes
const rootRoute = {
  path: "/",
  exact: true,
  component: () => <HomePage />,
  route: PrivateRoute,
};

// Auth
const authRoutes = {
  path: "/account",
  name: "Auth",
  children: [
    {
      path: "/account/login",
      name: "Login",
      component: Login,
      route: Route,
    },
    {
      path: "/account/logout",
      name: "Logout",
      component: Logout,
      route: Route,
    },
    // {
    //   path: "/account/register",
    //   name: "Register",
    //   component: Register,
    //   route: Route,
    // },
    {
      path: "/account/confirm-password",
      name: "Confirm",
      component: ConfirmPassword,
      route: Route,
    },
    {
      path: "/account/forget-password",
      name: "Forget Password",
      component: ForgetPassword,
      route: Route,
    },
    {
      path: "/account",
      name: "Profile",
      component: UserInfo,
      route: PrivateRoute,
    },
  ],
};

const PackagesRouter = [
  {
    name: "Quản lý gói",
    path: "/bilet/packages/list",
    component: PackagesList,
    route: PrivateRoute,
    roles: ["viewListPackages"],
  },
  {
    path: "/bilet/packages/add",
    component: PackagesAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewPackages",
      "addPackages",
      "editPackages",
      "deletePackages",
      "statusPackages",
      "importPackages",
    ],
  },
  {
    pathParam: "/bilet/packages/:id/:action",
    component: PackagesAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewPackages",
      "addPackages",
      "editPackages",
      "deletePackages",
      "statusPackages",
      "importPackages",
    ],
  },
  {
    pathParam: "/bilet/packages/:id",
    component: PackagesAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewPackages",
      "addPackages",
      "editPackages",
      "deletePackages",
      "statusPackages",
      "importPackages",
    ],
  },
];

const BookRouter = [
  {
    name: "Bộ từ vựng",
    path: "/bilet/book/list",
    component: BookList,
    route: PrivateRoute,
    roles: ["viewListBook"],
  },
  {
    path: "/bilet/book/add",
    component: BookAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewBook",
      "addBook",
      "editBook",
      "deleteBook",
      "statusBook",
      "importBook",
    ],
  },
  {
    pathParam: "/bilet/book/:id/:action",
    component: BookAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewBook",
      "addBook",
      "editBook",
      "deleteBook",
      "statusBook",
      "importBook",
    ],
  },
  {
    pathParam: "/bilet/book/:id",
    component: BookAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewBook",
      "addBook",
      "editBook",
      "deleteBook",
      "statusBook",
      "importBook",
    ],
  },
];

const MenuWebsiteRouter = [
  {
    name: "Quản lý menu",
    path: "/website/news/menu",
    component: MenuShow,
    route: PrivateRoute,
  },
];

const NewsCategoryRouter = [
  {
    name: "Danh mục tin tức",
    path: "/website/news/category/list",
    component: CategoryList,
    roles: ["viewListCategory"],
    route: PrivateRoute,
  },
  {
    path: "/website/news/category/add",
    component: CategoryAdd,
    route: PrivateRoute,
    hide: true,
  },
  {
    pathParam: "/website/news/category/:id/:action",
    component: CategoryAdd,
    route: PrivateRoute,
    hide: true,
  },
  {
    pathParam: "/website/news/category/:id",
    component: CategoryAdd,
    route: PrivateRoute,
    hide: true,
  },
];
const PostRouter = [
  {
    name: "Danh sách tin tức",
    path: "/website/news/post/list",
    roles: ["viewListPages"],
    component: PostList,
    route: PrivateRoute,
  },
  {
    path: "/website/news/post/add",
    component: PostAdd,
    route: PrivateRoute,
    hide: true,
  },
  {
    pathParam: "/website/news/post/:id/:action",
    component: PostAdd,
    route: PrivateRoute,
    hide: true,
  },
  {
    pathParam: "/website/news/post/:id",
    component: PostAdd,
    route: PrivateRoute,
    hide: true,
  },
];

const DiaryRouter = [
  {
    name: "Sổ từ vựng",
    path: "/bilet/diary/list",
    component: DiaryList,
    route: PrivateRoute,
    roles: [
      "viewListDiary",
      "viewDiary",
      "addDiary",
      "editDiary",
      "deleteDiary",
      "statusDiary",
      "imporDiary",
    ],
  },
  {
    path: "/bilet/diary/add",
    component: DiaryList,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewListDiary",
      "viewDiary",
      "addDiary",
      "editDiary",
      "deleteDiary",
      "statusDiary",
      "imporDiary",
    ],
  },
];

const TopicCategoryRouter = [
  {
    name: "Cụm chủ đề",
    path: "/bilet/topic-category/list",
    component: TopicCategoryList,
    route: PrivateRoute,
    roles: ["viewListTopicCategory"],
  },
  {
    path: "/bilet/topic-category/add",
    component: TopicCategoryList,
    route: PrivateRoute,
    hide: true,
    roles: ["addTopicCategory"],
  },
];

const PairOfWordRouter = [
  {
    name: "Cặp từ",
    path: "/bilet/pair-of-word/list",
    component: PairOfWordList,
    route: PrivateRoute,
    roles: ["viewListPairOfWord"],
  },
  {
    path: "/bilet/pair-of-word/add",
    component: PairOfWordAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewPairOfWord",
      "addPairOfWord",
      "editPairOfWord",
      "deletePairOfWord",
      "statusPairOfWord",
      "importPairOfWord",
    ],
  },
  {
    pathParam: "/bilet/pair-of-word/:id/:action",
    component: PairOfWordAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewPairOfWord",
      "addPairOfWord",
      "editPairOfWord",
      "deletePairOfWord",
      "statusPairOfWord",
      "importPairOfWord",
    ],
  },
  {
    pathParam: "/bilet/pair-of-word/:id",
    component: PairOfWordAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewPairOfWord",
      "addPairOfWord",
      "editPairOfWord",
      "deletePairOfWord",
      "statusPairOfWord",
      "importPairOfWord",
    ],
  },
];

const QuizRouter = [
  {
    name: "Thực hành",
    path: "/bilet/quiz",
    component: QuizList,
    route: PrivateRoute,
    roles: ["viewListQuiz"],
    children: [
      {
        path: "/bilet/quiz/list/all",
        pathParam: "/bilet/quiz/list/:question_type",
        name: "Tất cả",
        component: QuizList,
        roles: ["viewListQuiz"],
        route: PrivateRoute,
      },
      ...QuestionsType?.map((item: any) => {
        return {
          path: `/bilet/quiz/list/${item.value}`,
          pathParam: "/bilet/quiz/list/:question_type",
          name: item.label,
          component: QuizList,
          roles: ["viewListQuiz"],
          route: PrivateRoute,
        };
      }),
    ],
  },
  {
    pathParam: "/bilet/quiz/:idQuiz/question/:question_type/add",
    component: QuestionAdd,
    route: PrivateRoute,
    hide: true,
    roles: ["viewListQuestion"],
  },
  {
    pathParam: "/bilet/quiz/:idQuiz/question/:question_type/:id/:action",
    component: QuestionAdd,
    route: PrivateRoute,
    hide: true,
    roles: ["viewListQuestion"],
  },
  {
    pathParam: "/bilet/quiz/:idQuiz/question/:question_type/:id",
    component: QuestionAdd,
    route: PrivateRoute,
    hide: true,
    roles: ["viewListQuestion"],
  },

  {
    pathParam: "/bilet/quiz/:id/questions/:question_type",
    component: QuestionList,
    route: PrivateRoute,
    hide: true,
    roles: ["viewListQuestion"],
  },
];

const SentenceRouter = [
  {
    name: "Câu (Giao tiếp/Nổi tiếng)",
    path: "/bilet/sentence/list",
    component: SentenceList,
    route: PrivateRoute,
    roles: ["viewListSentence"],
  },
  {
    path: "/bilet/sentence/add",
    component: SentenceAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewSentence",
      "addSentence",
      "editSentence",
      "deleteSentence",
      "statusSentence",
      "importSentence",
    ],
  },
  {
    pathParam: "/bilet/sentence/:id/:action",
    component: SentenceAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewSentence",
      "addSentence",
      "editSentence",
      "deleteSentence",
      "statusSentence",
      "importSentence",
    ],
  },
  {
    pathParam: "/bilet/sentence/:id",
    component: SentenceAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewSentence",
      "addSentence",
      "editSentence",
      "deleteSentence",
      "statusSentence",
      "importSentence",
    ],
  },
];

const StudentRouter = [
  {
    name: "Học viên",
    path: "/bilet/student/list",
    component: StudentList,
    route: PrivateRoute,
    roles: ["viewListStudent"],
  },
  {
    pathParam: "/bilet/student/:id/:action",
    component: StudentDetail,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewStudent",
      "addStudent",
      "editStudent",
      "deleteStudent",
      "statusStudent",
    ],
  },
  {
    pathParam: "/bilet/student/:id",
    component: StudentDetail,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewStudent",
      "addStudent",
      "editStudent",
      "deleteStudent",
      "statusStudent",
    ],
  },
];

const TeacherRouter = [
  {
    name: "Giáo viên",
    path: "/bilet/teacher/list",
    component: (props: any) => <StudentList {...props} is_teacher={true} />,
    route: PrivateRoute,
    roles: ["viewListStudent"],
  },
  {
    pathParam: "/bilet/teacher/:id",
    component: (props: any) => <StudentDetail {...props} is_teacher={true} />,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewStudent",
      "addStudent",
      "editStudent",
      "deleteStudent",
      "statusStudent",
    ],
  },
  {
    pathParam: "/bilet/teacher/:id/view",
    component: (props: any) => <StudentDetail {...props} is_teacher={true} />,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewStudent",
      "addStudent",
      "editStudent",
      "deleteStudent",
      "statusStudent",
    ],
  },
];

const WordRequestRouter = [
  {
    name: "Học viên đóng góp",
    path: "/bilet/pair-of-word-request/list",
    component: PairOfWordRequestList,
    route: PrivateRoute,
    roles: ["viewListaddPairOfWordRequest"],
  },
];

const UsersRouter = {
  path: "/user",
  pathParam: "/user/list",
  name: "Quản lý nhân viên",
  icon: FeatherIcon.User,
  roles: ["viewListUser"],
  children: [
    {
      path: "/user/list",
      component: UsersList,
      route: PrivateRoute,
      hide: true,
      roles: ["viewListUser"],
    },
    {
      pathParam: "/user/:id/:action",
      component: UsersDetail,
      route: PrivateRoute,
      hide: true,
      roles: ["viewUser", "addUser", "editUser", "deleteUser", "statusUser"],
    },
    {
      pathParam: "/user/:id",
      component: UsersDetail,
      route: PrivateRoute,
      hide: true,
      roles: ["viewUser", "addUser", "editUser", "deleteUser", "statusUser"],
    },
  ],
};

const RolesRouter = {
  path: "/roles",
  pathParam: "/roles/list",
  name: "Quyền truy cập",
  icon: FeatherIcon.Cpu,
  roles: ["viewListRole"],
  children: [
    {
      path: "/roles/list",
      component: RolesList,
      route: PrivateRoute,
      hide: true,
      roles: ["viewListRole"],
    },
    {
      pathParam: "/roles/:id/:action",
      component: RoleAdd,
      route: PrivateRoute,
      hide: true,
      roles: [
        "viewRole",
        "viewRole",
        "addRole",
        "editRole",
        "deleteRole",
        "statusRole",
      ],
    },
    {
      pathParam: "/roles/:id",
      component: RoleAdd,
      route: PrivateRoute,
      hide: true,
      roles: [
        "viewRole",
        "viewRole",
        "addRole",
        "editRole",
        "deleteRole",
        "statusRole",
      ],
    },
  ],
};
const QuoteRouter = [
  {
    name: "Câu nổi tiếng",
    path: "/bi_admin/quote/list",
    component: QuoteList,
    route: PrivateRoute,
    roles: ["viewListQuote"],
  },
  {
    path: "/bi_admin/quote/add",
    component: QuoteAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewQuote",
      "addQuote",
      "editQuote",
      "deleteQuote",
      "statusQuote",
      "importQuote",
    ],
  },
  {
    pathParam: "/bi_admin/quote/:id/:action",
    component: QuoteAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewQuote",
      "addQuote",
      "editQuote",
      "deleteQuote",
      "statusQuote",
      "importQuote",
    ],
  },
  {
    pathParam: "/bi_admin/quote/:id",
    component: QuoteAdd,
    route: PrivateRoute,
    hide: true,
    roles: [
      "viewQuote",
      "addQuote",
      "editQuote",
      "deleteQuote",
      "statusQuote",
      "importQuote",
    ],
  },
];

const SaleGroupRouter = {
  icon: FeatherIcon.Users,
  name: "Quản lý nhóm",
  path: "/sale_group",
  pathParam: "/sale_group/list",
  component: SaleGroupList,
  roles: ["viewListSaleGroup"],
  children: [
    {
      path: "/sale_group/list",
      component: SaleGroupList,
      route: PrivateRoute,
      hide: true,
      roles: ["viewListSaleGroup"],
    },
    {
      pathParam: "/sale_group/:id/member/:member_id/:action",
      component: SaleGroupMember,
      route: PrivateRoute,
      hide: true,
      roles: [
        "viewSaleGroup",
        "addSaleGroup",
        "editSaleGroup",
        "deleteSaleGroup",
        "statusSaleGroup",
      ],
    },
    {
      pathParam: "/sale_group/:id/:action",
      component: SaleGroupDetail,
      route: PrivateRoute,
      hide: true,
      roles: [
        "viewSaleGroup",
        "addSaleGroup",
        "editSaleGroup",
        "deleteSaleGroup",
        "statusSaleGroup",
      ],
    },
    {
      pathParam: "/sale_group/:id",
      component: SaleGroupDetail,
      route: PrivateRoute,
      hide: true,
      roles: [
        "viewSaleGroup",
        "addSaleGroup",
        "editSaleGroup",
        "deleteSaleGroup",
        "statusSaleGroup",
      ],
    },
  ],
};

const CommissionRouter = {
  icon: FeatherIcon.PieChart,
  name: "Quản lý hoa hồng",
  path: "/commission",
  pathParam: "/commission/list",
  component: CommissionList,
  roles: ["viewListSaleGroup"],
  children: [
    {
      path: "/commission/list",
      component: CommissionList,
      route: PrivateRoute,
      hide: true,
      roles: ["viewListCommission"],
    },
    {
      pathParam: "/commission/:id/:action",
      component: CommissionDetail,
      route: PrivateRoute,
      hide: true,
      roles: [
        "viewCommission",
        "addCommission",
        "editCommission",
        "deleteCommission",
        "statusCommission",
      ],
    },
    {
      pathParam: "/commission/:id",
      component: CommissionDetail,
      route: PrivateRoute,
      hide: true,
      roles: [
        "viewCommission",
        "addCommission",
        "editCommission",
        "deleteCommission",
        "statusCommission",
      ],
    },
  ],
};

const WebsiteRouter = {
  path: "/webiste",
  pathParam: "/webiste/list",
  name: "Quản trị website",
  icon: FeatherIcon.Aperture,
  children: [
    ...NewsCategoryRouter,
    ...PostRouter,
    // ...MenuWebsiteRouter
  ],
};

const BiletCenterRouter = {
  path: "/bilet",
  pathParam: "/bilet/list",
  name: "Bilet Center",
  icon: FeatherIcon.Grid,
  roles: [
    "viewListBook",
    "viewListDiary",
    "viewListTopicCategoryList",
    "viewListPairOfWord",
    "viewListSentence",
    "viewListStudent",
    "viewListaddPairOfWordRequest",
  ],
  children: [
    ...BookRouter,
    ...DiaryRouter,
    ...TopicCategoryRouter,
    ...PairOfWordRouter,
    ...SentenceRouter,
    ...QuizRouter,
    ...StudentRouter,
    ...TeacherRouter,
    ...PackagesRouter,
    ...WordRequestRouter,
  ],
};

const ReportRouter = {
  path: "/report",
  pathParam: "/report/list",
  name: "Báo cáo",
  icon: FeatherIcon.BarChart2,
  roles: ["viewListReport"],
  children: [
    {
      name: "Báo cáo doanh thu",
      path: "/report/revenues",
      component: ReportRevenues,
      route: PrivateRoute,
      roles: ["viewListReport"],
    },
    {
      name: "Báo cáo đơn hàng",
      path: "/report/orders",
      component: ReportOrders,
      route: PrivateRoute,
      roles: ["viewListReport"],
    },
    {
      name: "Báo cáo hoa hồng",
      path: "/report/commission",
      component: ReportCommission,
      route: PrivateRoute,
      roles: ["viewListReport"],
    },
  ],
};

const AdministrationRouter = {
  path: "/bi_admin",
  pathParam: "/bi_admin/list",
  name: "Quản trị",
  icon: FeatherIcon.Shield,
  roles: ["updateSystemConfig", "viewListFAQ", "viewListQuote"],
  children: [
    ...QuoteRouter,
    {
      path: "/bi_admin/app_configs/:action",
      component: AppConfigs,
      route: PrivateRoute,
      hide: true,
      // roles: ["viewConfigs"],
    },
    {
      name: "Quản trị App",
      path: "/bi_admin/app_configs/view",
      component: AppConfigs,
      roles: ["updateSystemConfig"],
      route: PrivateRoute,
      // roles: ["viewConfigs"],
    },
    {
      name: "FAQ",
      path: "/bi_admin/faq/list",
      component: FAQList,
      route: PrivateRoute,
      roles: ["viewListFAQ"],
    },
    {
      path: "/bi_admin/faq/add",
      hide: true,
      component: FAQAdd,
      route: PrivateRoute,
      roles: ["viewFAQ", "addFAQ", "editFAQ", "statusFAQ"],
    },
    {
      pathParam: "/bi_admin/faq/:id/:action",
      component: FAQAdd,
      route: PrivateRoute,
      hide: true,
      roles: ["viewFAQ", "addFAQ", "editFAQ", "statusFAQ"],
    },
    {
      pathParam: "/bi_admin/faq/:id",
      component: FAQAdd,
      route: PrivateRoute,
      hide: true,
      roles: ["viewFAQ", "addFAQ", "editFAQ", "statusFAQ"],
    },
  ],
};

const OrdersRouter = {
  path: "/orders",
  pathParam: "/orders/list",
  name: "Quản lý đơn hàng",
  icon: FeatherIcon.Clipboard,
  roles: ["viewListOrders"],
  children: [
    {
      name: "Đơn hàng",
      path: "/orders/list",
      component: OrdersList,
      route: PrivateRoute,
      roles: ["viewListOrders"],
    },
    {
      name: "Thêm đơn hàng",
      path: "/orders/add",
      component: OrdersAdd,
      route: PrivateRoute,
      roles: ["viewOrders", "addOrders", "editOrders", "statusOrders"],
    },
    {
      pathParam: "/orders/:id/:action",
      component: OrdersAdd,
      route: PrivateRoute,
      hide: true,
      roles: ["viewOrders", "addOrders", "editOrders", "statusOrders"],
    },
    {
      pathParam: "/orders/:id",
      component: OrdersAdd,
      route: PrivateRoute,
      hide: true,
      roles: ["viewOrders", "addOrders", "editOrders", "statusOrders"],
    },
  ],
};

// notFound
const notFoundRoute = {
  path: "*",
  exact: true,
  component: NotFound,
  route: PrivateRoute,
};

const appRoutes = [
  UsersRouter,
  BiletCenterRouter,
  SaleGroupRouter,
  CommissionRouter,
  ReportRouter,
  OrdersRouter,
  AdministrationRouter,
  RolesRouter,
  WebsiteRouter,
];

// flatten the list of all nested routes
interface routesItem {
  children: any;
}

const flattenRoutes = (routes?: routesItem[]) => {
  let flatRoutes: any = [];

  routes = routes || [];
  routes.forEach((item) => {
    flatRoutes.push(item);
    if (typeof item.children !== "undefined") {
      flatRoutes = [...flatRoutes, ...flattenRoutes(item.children)];
    }
  });
  return flatRoutes;
};

// All routes
const allRoutes: any = [rootRoute, ...appRoutes, authRoutes, notFoundRoute];

const authProtectedRoutes = [...appRoutes];
// const authProtectedRoutes = [ ...appRoutes];
const allFlattenRoutes = flattenRoutes(allRoutes);
export { allRoutes, authProtectedRoutes, allFlattenRoutes };
